import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PiggyBank, TrendingUp, Coins, Users } from "lucide-react";
import { usePricing, finalCashbackRate, cad } from "@/lib/pricing";
import { FeeReviewButton, FeeReviewsCard } from "@/components/merchant/FeeReview";
import { StatusChip } from "@/components/sponsored/format";

export default function MerchantCashback() {
  const pricing = usePricing();
  const cashbackRate = finalCashbackRate(pricing);
  const [data, setData] = useState(null);
  const [conversions, setConversions] = useState(null);
  const [days, setDays] = useState(30);

  useEffect(() => {
    api.get(`/merchant/cashback/summary?days=${days}`).then((r) => setData(r.data));
    api.get(`/merchant/billing/conversions?days=${days}`).then((r) => setConversions(r.data));
  }, [days]);

  const [reviews, setReviews] = useState({ enabled: false, rows: [] });
  const [reasons, setReasons] = useState([]);
  const loadReviews = () => api.get("/merchant/performance-fee-reviews").then((r) => setReviews(r.data)).catch(() => {});
  useEffect(() => {
    loadReviews();
    api.get("/performance-fee/policy").then((r) => setReasons(r.data.review_reasons || [])).catch(() => {});
  }, []);
  const reviewFor = (id) => reviews.rows.find((x) => x.verified_conversion_id === id && x.status === "REQUESTED")
    || reviews.rows.find((x) => x.verified_conversion_id === id);

  if (!data || !conversions) return <div className="text-slate-500 text-sm">Loading…</div>;
  const c = conversions.totals;
  const maxCampaignCB = Math.max(1, ...(data.by_campaign || []).map((x) => x.cashback));

  return (
    <div data-testid="merchant-cashback">
      <div className="pf-chip pf-chip-mint"><PiggyBank className="w-3 h-3" /> Cashback</div>
      <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight mt-3 text-slate-900">Cashback issued</h2>
      <p className="text-sm text-slate-600 mt-1 max-w-2xl" data-testid="merchant-cashback-rate-copy">Automatic Cyannect rewards paid to your shoppers after every verified purchase — {cashbackRate ? `${cashbackRate} on every verified order, funded by Cyannect and the same on every plan.` : <>0.75% on orders ≤ CA$200, 1% on orders &gt; CA$200.</>}</p>

      <div className="flex gap-2 mt-6">
        {[7, 30, 90].map((d) => (
          <button key={d} onClick={() => setDays(d)} className={`px-3 py-1.5 rounded-full text-xs font-semibold ${days === d ? "pf-btn-primary" : "pf-btn-glass"}`} data-testid={`mcb-range-${d}`}>Last {d}d</button>
        ))}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
        <div className="pf-card p-5" data-testid="mcb-kpi-issued">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1"><Coins className="w-3 h-3" /> Cashback issued</div>
          <div className="font-display text-2xl font-medium mt-2 tabular-nums pf-gradient-text-2">{cad((data.total_cashback_issued || 0))}</div>
          <div className="text-[10px] text-slate-500 mt-1">{data.count} transactions</div>
        </div>
        <div className="pf-card p-5" data-testid="mcb-kpi-avg">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1"><TrendingUp className="w-3 h-3" /> Avg cashback</div>
          <div className="font-display text-2xl font-medium mt-2 tabular-nums">{cad((data.average_cashback || 0))}</div>
        </div>
        <div className="pf-card p-5" data-testid="mcb-kpi-conversions">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1"><Users className="w-3 h-3" /> Verified conversions</div>
          <div className="font-display text-2xl font-medium mt-2 tabular-nums">{c.count}</div>
          <div className="text-[10px] text-slate-500 mt-1">{c.voided_count} voided</div>
        </div>
        <div className="pf-card p-5" data-testid="mcb-kpi-fees">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Performance fees due</div>
          <div className="font-display text-2xl font-medium mt-2 tabular-nums">{cad((c.fees_total || 0))}</div>
          <div className="text-[10px] text-slate-500 mt-1">
            {pricing?.final_active
              ? `${c.breakdown?.standard || 0} verified · your plan's fee, no per-sale cap`
              : <>{c.breakdown?.standard || 0} @ 4.99%{c.breakdown?.capped ? ` · ${c.breakdown.capped} capped @ CA$39.99` : ""}</>}
          </div>
        </div>
      </div>

      <div className="pf-card p-5 mt-6" data-testid="mcb-by-campaign">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-3">Cashback by campaign</div>
        {(data.by_campaign || []).length === 0 ? (
          <div className="text-xs text-slate-400 italic">No cashback issued in this window yet.</div>
        ) : (
          data.by_campaign.map((r) => {
            const pct = (r.cashback / maxCampaignCB) * 100;
            return (
              <div key={r.campaign_id} className="mb-3 last:mb-0" data-testid={`mcb-campaign-${r.campaign_id}`}>
                <div className="flex justify-between text-xs text-slate-600 mb-1">
                  <span className="truncate max-w-[70%]">{r.name || "Untitled"}</span>
                  <span className="font-mono">{cad(r.cashback)} · {r.count}×</span>
                </div>
                <div className="h-2 rounded-full bg-white/60 overflow-hidden">
                  <div className="h-2 rounded-full bg-gradient-to-r from-cyan-400 to-emerald-400" style={{ width: `${Math.max(4, pct)}%` }} />
                </div>
              </div>
            );
          })
        )}
      </div>

      <div className="pf-card p-5 mt-6" data-testid="mcb-ledger">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-3">Verified conversions ledger</div>
        {conversions.conversions.length === 0 ? (
          <div className="text-xs text-slate-400 italic">No verified conversions in this window.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-widest text-slate-500">
                <tr><th className="pb-2 pr-3">Order</th><th className="pb-2 pr-3">Sale</th><th className="pb-2 pr-3">Fee</th><th className="pb-2 pr-3">Coupon</th><th className="pb-2 pr-3">Method</th><th className="pb-2 pr-3">Status</th><th className="pb-2 pr-3">When</th>{reviews.enabled && <th className="pb-2 pr-3">Fee review</th>}</tr>
              </thead>
              <tbody>
                {conversions.conversions.slice(0, 25).map((r) => (
                  <tr key={r.id} className="border-t border-white/60" data-testid={`mcb-conv-${r.id}`}>
                    <td className="py-2 pr-3 font-mono text-xs">{r.order_id.slice(0, 16)}…</td>
                    <td className="py-2 pr-3 tabular-nums">{cad(r.sale_amount)}</td>
                    <td className={`py-2 pr-3 tabular-nums ${r.fee_reversed ? "text-slate-400 line-through" : "text-cyan-700"}`} data-testid={`mcb-fee-${r.id}`}>{cad(r.applied_fee)}</td>
                    <td className="py-2 pr-3 font-mono text-xs">{r.coupon_code || "—"}</td>
                    <td className="py-2 pr-3 text-xs text-slate-500">{r.verification_method}</td>
                    <td className="py-2 pr-3"><span className={`pf-chip ${r.status === "verified" ? "pf-chip-mint" : ""}`}>{r.status}</span></td>
                    <td className="py-2 pr-3 text-xs text-slate-500">{new Date(r.created_at).toLocaleString()}</td>
                    {reviews.enabled && <td className="py-2 pr-3"><span className="inline-flex items-center gap-2">
                      {reviewFor(r.id) && <StatusChip status={reviewFor(r.id).status} testid={`fee-review-status-${r.id}`} />}
                      {r.fee_reversed ? <span className="text-xs text-slate-500" data-testid={`fee-reversed-${r.id}`}>Fee reversed</span>
                        : !reviewFor(r.id) && (r.status === "verified" || r.fee_retained) && <FeeReviewButton conv={r} reasons={reasons} onDone={loadReviews} />}
                    </span></td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {reviews.enabled && <FeeReviewsCard rows={reviews.rows} reasons={reasons} />}
    </div>
  );
}
